import { useCallback, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";
import { useLastTurnedRolls } from "../hooks/useLastTurnedRolls";
import type { LastTurnedRollRow } from "../models/LastTurnedRolls";
import { LastTurnedRollsSummary } from "./LastTurnedRollsSummary";
import { LastTurnedRollsToolbar } from "./LastTurnedRollsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const LEFT_CELL = { textAlign: "left" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

const dateOnly = (value: unknown): string =>
	value ? String(value).substring(0, 10) : "";

const columnDefs: ColDef<LastTurnedRollRow>[] = [
	{
		field: "TimestampString",
		headerName: "Date Turned",
		width: 140,
		sortable: true,
		sort: "asc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => dateOnly(p.value),
	},
	{
		field: "RollNumber",
		headerName: "Rollset",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollName",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "AmountTurnedOff",
		headerName: "Amount Turned Off",
		width: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "TimeToCutString",
		headerName: "Turn Time",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Diameter",
		headerName: "Turn Diameter",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "Operator1Name",
		headerName: "Turn by Name 1",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Operator2Name",
		headerName: "Turn by Name 2",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Comments",
		headerName: "Turn Notes",
		flex: 1,
		minWidth: 200,
		sortable: true,
		cellStyle: LEFT_CELL,
		tooltipField: "Comments",
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
};

export const LastTurnedRollsReport = () => {
	const [{ start, end }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<LastTurnedRollRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useLastTurnedRolls({
		start: startStr,
		end: endStr,
	});

	const rows = dataQuery.data ?? [];

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Last_Turned_Rolls_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "TimestampString") {
					return dateOnly(params.value);
				}
				if (colId === "AmountTurnedOff" || colId === "Diameter") {
					return toFixedOrEmpty(params.value, 3);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportToPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			sums: [{ colId: "AmountTurnedOff", label: "Total Turned Off" }],
			aligns: {
				Comments: "left",
				AmountTurnedOff: "right",
				Diameter: "right",
			},
		});
		exportPdf(
			`Last Turned Rolls ${start.format("MM-DD-YYYY")} to ${end.format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "Last Turned Rolls",
					filters: [
						{ label: "Start Date", value: start.format("MM/DD/YYYY") },
						{ label: "Close Date", value: end.format("MM/DD/YYYY") },
					],
					tables: [table],
				}),
		);
	};

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Last Turned Rolls
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<LastTurnedRollsToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onExportExcel={handleExportToExcel}
					onExportPdf={handleExportToPdf}
					canExportPdf={!dataQuery.isLoading && rows.length > 0}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			<LastTurnedRollsSummary
				total={rows.length}
				money={{
					label: "Total Turned Off",
					value: rows.reduce(
						(sum, row) => sum + (Number(row.AmountTurnedOff) || 0),
						0,
					),
				}}
			/>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<LastTurnedRollRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
